import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import SolicitudesDropdown from './common/SolicitudesDropdown';
import UserSessionMenu from './common/UserSessionMenu';
import ColegioService from '../services/ColegioService';
import ParticularService from '../services/ParticularService';
import materiaService from '../services/MateriaService';
import AsistenteService from '../services/AsistenteService';
import { hasOpenAIChatsEnabled, isAdminColegio, isFeatureEnabled, normalizeRoleName } from '../utils/adminMenu';
import solicitudesService from '../services/SolicitudesService';
import particularesApiService from '../services/ParticularesApiService';
import './NewAssistant.css';
import ProfessorMenu from './common/menus/ProfessorMenu';
import AdminMenu from './common/menus/AdminMenu';
import ParticularDashboardMenu from './common/menus/ParticularDashboardMenu';
import { isParticularChatGPTAllowed } from '../utils/particularChatGPTAccess';

const VALIDATION_ERROR_MESSAGE = 'No pudimos procesar los archivos. Verifica que todos sean PDF válidos y que no superen los límites permitidos.';
const GPT_CLASICO_NOMBRE_MAX_LENGTH = 50;
const PROMPT_FEATURES = [
  'Usa exclusivamente los PDFs adjuntos (RAG obligatorio).',
  'Ejecuta file_search antes de responder consultas académicas.',
  'Si no hay evidencia en los PDFs, responde con el mensaje de falta de información.',
  'Respuestas académicas en HTML con citas de PDF y número de página.',
  'Puede responder saludos o uso del asistente sin citar PDFs.',
  'Imágenes o esquemas solo si existen en PDFs o son representaciones fieles con aviso.',
  'Puede armar planes de estudio y actuar como tutor orientado a ayudar al alumno.'
];

const PROMPT_EXTRA_MARKER = 'ACLARACIONES EXTRAS QUE DEBES CUMPLIR:';

const extractPromptExtra = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  const normalized = value.trim();
  const markerIndex = normalized.toUpperCase().indexOf(PROMPT_EXTRA_MARKER);
  if (markerIndex < 0) {
    return '';
  }

  return normalized.slice(markerIndex + PROMPT_EXTRA_MARKER.length).trim();
};

const looksLikeGeneratedPrompt = (value) => {
  if (!value || typeof value !== 'string') {
    return false;
  }

  const normalized = value.toUpperCase();
  return normalized.includes('ERES UN ASISTENTE ACADÉMICO ESPECIALIZADO')
    || normalized.includes('FUENTES DE INFORMACIÓN (OBLIGATORIO):')
    || normalized.includes(PROMPT_EXTRA_MARKER);
};

const resolveEditablePromptExtra = (asistente) => {
  const promptExtra = asistente?.promptExtra || asistente?.PromptExtra || '';
  const instrucciones = asistente?.gptClasicoInstrucciones || asistente?.GPTClasicoInstrucciones || '';

  const extractedFromPromptExtra = extractPromptExtra(promptExtra);
  if (extractedFromPromptExtra) {
    return extractedFromPromptExtra;
  }

  if (promptExtra.trim() && !looksLikeGeneratedPrompt(promptExtra)) {
    return promptExtra.trim();
  }

  const extractedFromInstructions = extractPromptExtra(instrucciones);
  if (extractedFromInstructions) {
    return extractedFromInstructions;
  }

  return '';
};

const getStoredFileDisplayName = (value) => {
  if (!value || typeof value !== 'string') {
    return 'Archivo PDF';
  }

  const trimmed = value.trim();
  const fileName = trimmed.split(/[\\/]/).pop() || trimmed;
  return /^[a-f0-9]{32}_/i.test(fileName) ? fileName.slice(33) : fileName;
};

const getMateriaOptionLabel = (materia) => {
  const nombre = materia?.nombre || materia?.Nombre || 'Materia';
  const area = materia?.nombreArea || materia?.NombreArea || '';
  const valorAnio = materia?.valorAnio ?? materia?.ValorAnio ?? null;
  const division = String(materia?.division || materia?.Division || '').trim();
  const scope = valorAnio ? `${valorAnio}°${division ? ` ${division}` : ''}` : division;
  const parts = [area, nombre, scope].filter(Boolean);
  return parts.join(' · ');
};

const normalizeAssistantFiles = (files) => {
  if (!Array.isArray(files)) {
    return [];
  }

  return files
    .map((file) => {
      const id = file?.id || file?.Id || '';
      const nombreArchivo = file?.nombreArchivo || file?.NombreArchivo || '';
      const nombreVisible = file?.nombreVisible || file?.NombreVisible || getStoredFileDisplayName(nombreArchivo);

      if (!id || !nombreArchivo) {
        return null;
      }

      return {
        id,
        nombreArchivo,
        nombreVisible
      };
    })
    .filter(Boolean);
};

const getFriendlyApiErrorMessage = (error, fallback) => {
  if (!error || !error.message) {
    return fallback;
  }
  const normalized = error.message.toLowerCase();
  if (normalized.includes('validation')) {
    return VALIDATION_ERROR_MESSAGE;
  }
  return error.message;
};

const createInitialFormData = (idColegio = '') => ({
  gptClasicoUrlPublica: '',
  promptExtra: '',
  idColegio,
  idArea: '',
  idMateria: '',
  idUnidad: '',
  archivosPdf: [],
  publico: true,
  passwordAsistente: '',
  esPropio: false,
  gptClasicoLogo: '',
  gptClasicoNombre: '',
  gptClasicoInstrucciones: '',
  gptClasicoFraseIniciarConversacion: '',
  gptClasicoDescripcion: ''
});

const NewClassicGptAgent = () => {
  const { assistantId } = useParams();
  const { user, logout, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const featureFlags = useMemo(() => ColegioService.getFeatureFlags(), []);
  const activeParticular = useMemo(() => ParticularService.getActiveParticular(), []);
  const roleName = normalizeRoleName(user);
  const requestedScope = location.state?.scope || new URLSearchParams(location.search).get('scope');
  const isExplicitParticularScope = requestedScope === 'particulares' || requestedScope === 'particular';
  const isAdmin = useMemo(() => roleName.includes('admin'), [roleName]);
  const isGeneralAdmin = useMemo(() => roleName.includes('admin') && !isAdminColegio(user), [roleName, user]);
  const isParticularContext = useMemo(() => (
    isExplicitParticularScope || roleName.includes('particular') || (isGeneralAdmin && Boolean(activeParticular))
  ), [activeParticular, isExplicitParticularScope, isGeneralAdmin, roleName]);
  const isEditMode = Boolean(assistantId);
  const hasModuloAlumnos = useMemo(() => {
    return isParticularContext || isFeatureEnabled(featureFlags, 'ModuloAlumnos');
  }, [featureFlags, isParticularContext]);
  const hasOpenAIChats = useMemo(() => {
    return hasOpenAIChatsEnabled(featureFlags);
  }, [featureFlags]);
  const hasAgentesClasicos = useMemo(() => {
    return isParticularContext
      ? isParticularChatGPTAllowed(activeParticular)
      : isFeatureEnabled(featureFlags, 'PermiteAgentesClasicosGPT');
  }, [activeParticular, featureFlags, isParticularContext]);
  const userRoleLabel = user?.roleName || user?.nombreRol || user?.rol || user?.role || (isAdmin ? 'Admin' : 'Profesor');
  
  // Estados del componente
  const [isLoading, setIsLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [formData, setFormData] = useState(() => createInitialFormData());
  const [materias, setMaterias] = useState([]);
  const [materiasLoading, setMateriasLoading] = useState(false);
  const [materiasError, setMateriasError] = useState('');
  const [unidades, setUnidades] = useState([]);
  const [unidadesLoading, setUnidadesLoading] = useState(false);
  const [unidadesError, setUnidadesError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [existingFiles, setExistingFiles] = useState([]);
  const [removedExistingFileIds, setRemovedExistingFileIds] = useState([]);
  const remainingExistingFiles = useMemo(
    () => existingFiles.filter((file) => !removedExistingFileIds.includes(file.id)),
    [existingFiles, removedExistingFileIds]
  );
  const totalKnowledgeFiles = remainingExistingFiles.length + formData.archivosPdf.length;
  // Validar permisos al montar el componente
  useEffect(() => {
    if (authLoading) return;
    validateUserPermissions();
  }, [user, authLoading]);

  const validateUserPermissions = () => {
    try {
      if (!user) {
        setAlert({
          show: true,
          title: 'Sesión requerida',
          message: 'Debes iniciar sesión para acceder a esta funcionalidad',
          type: 'error'
        });
        setHasPermission(false);
        setIsLoading(false);
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      setHasPermission(true);
    } catch (error) {
      console.error('Error al validar permisos:', error);
      setHasPermission(false);
      setAlert({
        show: true,
        title: 'Error de permisos',
        message: 'Error al validar permisos. Inténtalo nuevamente.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isParticularContext) {
      setMaterias([]);
      setUnidades([]);
      setMateriasError('');
      setUnidadesError('');
      setFormData((prev) => ({ ...prev, idColegio: '', idArea: '', idMateria: '', idUnidad: '' }));
      return;
    }

    const activeColegioId = ColegioService.getActiveColegioId();
    setFormData((prev) => ({ ...prev, idColegio: activeColegioId || prev.idColegio }));
    if (!activeColegioId) return;

    const fetchMaterias = async () => {
      setMateriasLoading(true);
      setMateriasError('');
      try {
        const list = isAdmin
          ? await materiaService.getMateriasActivas({ idColegio: activeColegioId })
          : await materiaService.getMisMaterias({ idColegio: activeColegioId });
        setMaterias(Array.isArray(list) ? list : []);
      } catch (error) {
        setMateriasError(
          error.message || (isAdmin
            ? 'No pudimos cargar las materias activas del colegio.'
            : 'No pudimos cargar tus materias.')
        );
      } finally {
        setMateriasLoading(false);
      }
    };

    fetchMaterias();
  }, [isAdmin, isParticularContext]);

  useEffect(() => {
    if (!isEditMode || authLoading || !user || !assistantId) {
      return;
    }

    let isMounted = true;
    const fetchAssistant = async () => {
      setIsLoading(true);
      try {
        const response = isParticularContext
          ? await particularesApiService.getAsistenteById(assistantId)
          : await AsistenteService.getAsistenteById(assistantId);
        const asistente = response?.asistente || response?.Asistente || response;
        const archivosExistentes = asistente?.archivos || asistente?.Archivos || [];

        if (!isMounted || !asistente) return;

        setExistingFiles(normalizeAssistantFiles(archivosExistentes));
        setRemovedExistingFileIds([]);
        setFormData({
          ...createInitialFormData(
            asistente.idColegio || asistente.IdColegio || ColegioService.getActiveColegioId() || ''
          ),
          idColegio: asistente.idColegio || asistente.IdColegio || ColegioService.getActiveColegioId() || '',
          idArea: asistente.idArea || asistente.IdArea || '',
          idMateria: asistente.idMateria || asistente.IdMateria || '',
          idUnidad: '',
          publico: asistente.publico ?? asistente.Publico ?? true,
          passwordAsistente: asistente.passwordAsistente || asistente.PasswordAsistente || '',
          esPropio: asistente.esPropio ?? asistente.EsPropio ?? false,
          promptExtra: resolveEditablePromptExtra(asistente),
          gptClasicoUrlPublica: asistente.gptClasicoUrlPublica || asistente.GPTClasicoURLPublica || asistente.gptClasicoURLPublica || '',
          gptClasicoLogo: asistente.gptClasicoLogo || asistente.GPTClasicoLogo || '',
          gptClasicoNombre: asistente.gptClasicoNombre || asistente.GPTClasicoNombre || asistente.nombre || asistente.Nombre || '',
          gptClasicoInstrucciones: '',
          gptClasicoFraseIniciarConversacion: asistente.gptClasicoFraseIniciarConversacion || asistente.GPTClasicoFraseIniciarConversacion || '',
          gptClasicoDescripcion: asistente.gptClasicoDescripcion || asistente.GPTClasicoDescripcion || asistente.descripcion || asistente.Descripcion || ''
        });
      } catch (error) {
        if (!isMounted) return;
        setAlert({
          show: true,
          title: 'Error al cargar asistente IA',
          message: getFriendlyApiErrorMessage(error, 'No pudimos cargar el asistente IA a editar.'),
          type: 'error'
        });
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchAssistant();

    return () => {
      isMounted = false;
    };
  }, [assistantId, authLoading, isEditMode, isParticularContext, user]);

  useEffect(() => {
    if (isEditMode) return;
    setExistingFiles([]);
    setRemovedExistingFileIds([]);
  }, [isEditMode]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMateriaChange = (value) => {
    const selected = materias.find((m) => (m.id || m.Id) === value);
    const idArea = selected?.idArea || selected?.IdArea || '';
    setFormData((prev) => ({
      ...prev,
      idMateria: value,
      idArea,
      idUnidad: ''
    }));
  };

  // Manejar drag and drop de archivos
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    addFiles(files);
  };

  const addFiles = (newFiles) => {
    // Filtrar solo archivos PDF
    const pdfFiles = newFiles.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length !== newFiles.length) {
      setAlert({
        show: true,
        title: 'Archivos inválidos',
        message: 'Solo se permiten archivos PDF',
        type: 'warning'
      });
    }

    setFormData(prev => {
      const currentFiles = prev.archivosPdf;
      const totalFiles = [...currentFiles, ...pdfFiles];
      const totalAssociatedFiles = remainingExistingFiles.length + totalFiles.length;
      
      // Validar máximo 20 archivos
      if (totalAssociatedFiles > 20) {
        setAlert({
          show: true,
          title: 'Límite de archivos',
          message: 'El asistente IA no puede superar los 20 archivos PDF asociados.',
          type: 'error'
        });
        return prev; // No actualizar si excedel límite
      }
      
      return {
        ...prev,
        archivosPdf: totalFiles
      };
    });
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      archivosPdf: prev.archivosPdf.filter((_, i) => i !== index)
    }));
  };

  const toggleExistingFileRemoval = (fileId) => {
    if (!fileId) return;
    setRemovedExistingFileIds((prev) => (
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (!hasAgentesClasicos) {
      setAlert({
        show: true,
        title: 'Asistentes IA deshabilitados',
        message: 'La feature flag PermiteAgentesClasicosGPT está apagada para este colegio.',
        type: 'error'
      });
      return;
    }
    
    // Validaciones básicas
    const gptClasicoNombre = formData.gptClasicoNombre.trim();
    if (!gptClasicoNombre) {
      setAlert({
        show: true,
        title: 'Nombre requerido',
        message: 'El nombre del asistente IA es obligatorio',
        type: 'error'
      });
      return;
    }

    if (gptClasicoNombre.length > GPT_CLASICO_NOMBRE_MAX_LENGTH) {
      setAlert({
        show: true,
        title: 'Nombre demasiado largo',
        message: `El nombre del asistente IA no puede superar los ${GPT_CLASICO_NOMBRE_MAX_LENGTH} caracteres.`,
        type: 'error'
      });
      return;
    }

    if (!isParticularContext && materiasLoading) {
      setAlert({
        show: true,
        title: 'Materias cargando',
        message: 'Esperá a que carguen tus materias para continuar.',
        type: 'warning'
      });
      return;
    }

    if (totalKnowledgeFiles === 0) {
      setAlert({
        show: true,
        title: 'Archivos requeridos',
        message: 'Debes subir al menos un archivo PDF.',
        type: 'error'
      });
      return;
    }

    if (totalKnowledgeFiles > 20) {
      setAlert({
        show: true,
        title: 'Límite de archivos',
        message: 'El asistente IA no puede superar los 20 archivos PDF asociados.',
        type: 'error'
      });
      return;
    }
    if (!isParticularContext && !isEditMode && !formData.publico && !formData.passwordAsistente.trim()) {
      setAlert({
        show: true,
        title: 'Password requerido',
        message: 'Debes ingresar un password cuando el asistente IA es privado.',
        type: 'error'
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Mostrar mensaje inicial
      setAlert({
        show: true,
        title: isEditMode ? 'Enviando solicitud de edición' : 'Enviando solicitud',
        message: isEditMode
          ? 'Enviando solicitud de edición del asistente IA...'
          : 'Enviando solicitud... Este proceso puede demorar unos minutos debido a los archivos que se deben procesar.',
        type: 'info'
      });
      
      // Crear FormData para envío multipart
      const formDataToSend = new FormData();
      formDataToSend.append('gptClasicoNombre', gptClasicoNombre);
      if (isEditMode && assistantId) {
        formDataToSend.append('tipoSolicitud', 'edicion');
        formDataToSend.append('idOpenAIAssistant', assistantId);
      }
      if (formData.gptClasicoUrlPublica.trim()) {
        formDataToSend.append('gptClasicoUrlPublica', formData.gptClasicoUrlPublica.trim());
      }
      if (!isParticularContext && formData.idColegio) {
        formDataToSend.append('idColegio', formData.idColegio);
      }
      if (!isParticularContext && formData.idArea) {
        formDataToSend.append('idArea', formData.idArea);
      }
      if (!isParticularContext && formData.idMateria) {
        formDataToSend.append('idMateria', formData.idMateria);
      }
      if (!isParticularContext && !isEditMode && formData.idMateria) {
        formDataToSend.append('idUnidad', formData.idUnidad || '');
      }
      
      // Agregar todos los archivos PDF
      formData.archivosPdf.forEach(file => {
        formDataToSend.append('archivosPdf', file);
      });

      if (isEditMode && removedExistingFileIds.length > 0) {
        const archivosRemover = existingFiles
          .filter((file) => removedExistingFileIds.includes(file.id))
          .map((file) => ({
            id: file.id,
            nombreArchivo: file.nombreArchivo,
            nombreVisible: file.nombreVisible
          }));
        formDataToSend.append('archivosRemoverJson', JSON.stringify(archivosRemover));
      }
      formDataToSend.append('promptExtra', formData.promptExtra.trim());
      if (formData.gptClasicoLogo.trim()) {
        formDataToSend.append('gptClasicoLogo', formData.gptClasicoLogo.trim());
      }
      if (formData.gptClasicoFraseIniciarConversacion.trim()) {
        formDataToSend.append('gptClasicoFraseIniciarConversacion', formData.gptClasicoFraseIniciarConversacion.trim());
      }
      formDataToSend.append('gptClasicoModelo', '');
      if (formData.gptClasicoDescripcion.trim()) {
        formDataToSend.append('gptClasicoDescripcion', formData.gptClasicoDescripcion.trim());
      }
      formDataToSend.append('gptClasicoBusquedaEnLaWeb', 'false');
      formDataToSend.append('gptClasicoLienzo', 'false');
      formDataToSend.append('gptClasicoGeneracionDeImagenes', 'false');
      formDataToSend.append('gptClasicoInterpreteCodigoYAnalisisDatos', 'false');
      if (!isParticularContext && !isEditMode) {
        formDataToSend.append('esPropio', String(formData.esPropio));
        formDataToSend.append('publico', String(formData.publico));
        formDataToSend.append('conPassword', String(!formData.publico));
        if (!formData.publico && formData.passwordAsistente.trim()) {
          formDataToSend.append('passwordAsistente', formData.passwordAsistente.trim());
        }
      }

      if (isParticularContext) {
        await particularesApiService.createSolicitudGptClasico(formDataToSend);
      } else {
        await solicitudesService.crearSolicitudGptClasico(formDataToSend);
      }
      setAlert({ show: false, message: '', type: 'error', title: '' });
      setSuccessModalOpen(true);
      
      // Limpiar formulario después del éxito
      if (!isEditMode) {
        setTimeout(() => {
          setFormData(createInitialFormData(ColegioService.getActiveColegioId() || ''));
        }, 2000);
      }
      
    } catch (error) {
      console.error(`❌ Error al ${isEditMode ? 'crear solicitud de edición' : 'crear asistente IA'}:`, error);
      setAlert({
        show: true,
        title: isEditMode ? 'Error al crear solicitud de edición' : 'Error al crear asistente IA',
        message: getFriendlyApiErrorMessage(error, isEditMode
          ? 'No pudimos crear la solicitud de edición. Inténtalo nuevamente.'
          : 'No pudimos crear el asistente IA. Inténtalo nuevamente.'),
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!formData.idMateria) {
      setUnidades([]);
      setUnidadesError('');
      setUnidadesLoading(false);
      return;
    }

    let isMounted = true;
    setUnidadesLoading(true);
    setUnidadesError('');

    materiaService.getUnidadesActivas(formData.idMateria)
      .then((list) => {
        if (!isMounted) return;
        setUnidades(Array.isArray(list) ? list : []);
      })
      .catch((error) => {
        if (!isMounted) return;
        setUnidadesError(error.message || 'No pudimos cargar las unidades.');
        setUnidades([]);
      })
      .finally(() => {
        if (!isMounted) return;
        setUnidadesLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [formData.idMateria]);

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const closeSuccessModal = () => {
    setSuccessModalOpen(false);
    if (isParticularContext) {
      navigate('/particular');
    }
  };

  const handleGoBack = () => {
    navigate(isParticularContext ? '/particular' : '/mis-agentes-clasicos-gpt');
  };

  const handleLogout = () => {
    logout();
  };

  const handleChangeParticular = () => {
    ParticularService.setActiveParticular(null);
    navigate('/admin/select-colegio', { replace: true, state: { scope: 'particulares' } });
  };

  const pageTitle = isEditMode ? 'Editar asistente IA' : 'Crear asistente IA';
  const pageSubtitle = isEditMode
    ? 'Revisa la configuración actual, ajusta archivos si hace falta y envía una solicitud de edición para que el admin general la procese.'
    : 'Sube archivos PDF y define el comportamiento del asistente IA';
  const submitButtonDisabled = isSubmitting || totalKnowledgeFiles === 0;
  const submitButtonLabel = isEditMode ? 'Enviar Solicitud de Edición' : 'Enviar Solicitud';
  const submitLoadingLabel = isEditMode ? 'Enviando solicitud de edición...' : 'Enviando solicitud...';
  const successTitle = isEditMode ? 'Solicitud de edición creada' : 'Solicitud creada';
  const successMessage = isEditMode
    ? 'La solicitud de edición fue creada con éxito. El admin general podrá revisar los cambios del asistente IA, incluidos los archivos a agregar o quitar.'
    : 'La solicitud fue creada con éxito, y a la brevedad serás informado cuando el proceso de creación o actualización finalice.';

  // Mostrar loading mientras se validan permisos
  if (authLoading || isLoading) {
    return (
      <div className="new-assistant-container">
        <div className="loading-container">
          <LoadingSpinner message={isEditMode ? 'Cargando asistente IA...' : 'Validando permisos...'} />
        </div>
      </div>
    );
  }

  // Redirigir al dashboard si no tiene permisos (mostrar alerta allí)
  if (!hasPermission) {
    navigate(isAdmin ? '/admin-dashboard' : '/dashboard', { replace: true, state: { deniedCreateAssistant: true } });
    return null;
  }

  if (!hasAgentesClasicos) {
    return (
      <div className={`new-dashboard ${isParticularContext ? 'pd-pro' : ''}`}>
        <aside className="dashboard-sidebar">
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <i className="fas fa-graduation-cap"></i>
              <span>aluxIA</span>
            </div>
          </div>

          {isParticularContext ? (
            <ParticularDashboardMenu
              activeId="asistentes"
              onNavigate={(item) => item?.path && navigate(item.path, item.state ? { state: item.state } : undefined)}
              externalNavigation
              messageScope="particular"
            />
          ) : isAdmin ? (
            <AdminMenu
              activeId="agentes-clasicos-gpt"
              user={user}
              featureFlags={featureFlags}
              onNavigate={(item) => {
                if (!item) return;
                if (item.path) {
                  navigate(item.path, item.state ? { state: item.state } : undefined);
                  return;
                }
                if (item.id === 'agentes-clasicos-gpt') {
                  navigate('/mis-agentes-clasicos-gpt');
                  return;
                }
                navigate('/admin-dashboard', { state: { tab: item.id } });
              }}
            />
          ) : (
            <ProfessorMenu
              activeId="agentes-clasicos-gpt"
              hasOpenAIChats={hasOpenAIChats}
              hasAgentesClasicos={hasAgentesClasicos}
              hasModuloAlumnos={hasModuloAlumnos}
              onNavigate={(item) => item?.path && navigate(item.path)}
            />
          )}

          <div className="sidebar-footer">
            <div className="sidebar-user">
              <div className="user-avatar">
                <i className="fas fa-user"></i>
              </div>
              <div className="user-info">
                <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
                <span className="user-role">{userRoleLabel}</span>
              </div>
            </div>
          </div>
        </aside>

        <main className="dashboard-main">
          <header className="dashboard-header">
            <div className="header-left">
              <h1 className="page-title">
                <i className="fas fa-robot"></i>
                {pageTitle}
              </h1>
              <p className="page-subtitle">La funcionalidad de asistentes IA está deshabilitada para este colegio.</p>
            </div>

            <div className="header-right">
              <SolicitudesDropdown />
              {!isParticularContext && <ActiveColegioBadge />}
              <button className="logout-btn-header" onClick={handleLogout} title="Cerrar Sesión">
                <i className="fas fa-sign-out-alt"></i>
                <span className="logout-text">Salir</span>
              </button>
            </div>
          </header>

          <div className="dashboard-content">
            <div className="content-card">
              <div className="card-content">
                <div className="empty-state">
                  <div className="empty-state-content">
                    <i className="fas fa-ban empty-state-icon"></i>
                    <h3>Asistentes IA deshabilitados</h3>
                    <p>La funcionalidad de asistentes IA está apagada para este colegio.</p>
                    <button type="button" className="btn btn-primary" onClick={() => navigate('/mis-agentes-clasicos-gpt')}>
                      Volver a asistentes IA
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Mostrar formulario de creación si tiene permisos
  return (
    <div className={`new-dashboard ${isParticularContext ? 'pd-pro' : ''}`}>
      {/* Sidebar */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>aluxIA</span>
          </div>
        </div>
        
        {isParticularContext ? (
          <ParticularDashboardMenu
            activeId="asistentes"
            onNavigate={(item) => item?.path && navigate(item.path, item.state ? { state: item.state } : undefined)}
            externalNavigation
            messageScope="particular"
          />
        ) : isAdmin ? (
          <AdminMenu
            activeId="agentes-clasicos-gpt"
            user={user}
            featureFlags={featureFlags}
            onNavigate={(item) => {
              if (!item) return;
              if (item.path) {
                navigate(item.path, item.state ? { state: item.state } : undefined);
                return;
              }
              if (item.id === 'agentes-clasicos-gpt') {
                navigate('/mis-agentes-clasicos-gpt');
                return;
              }
              navigate('/admin-dashboard', { state: { tab: item.id } });
            }}
          />
        ) : (
          <ProfessorMenu
            activeId="agentes-clasicos-gpt"
            hasOpenAIChats={hasOpenAIChats}
            hasAgentesClasicos={hasAgentesClasicos}
            hasModuloAlumnos={hasModuloAlumnos}
            onNavigate={(item) => item?.path && navigate(item.path)}
          />
        )}
        
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{userRoleLabel}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">
              <i className="fas fa-robot"></i>
              {pageTitle}
            </h1>
            <p className="page-subtitle">{pageSubtitle}</p>
          </div>
          
          <div className="header-right">
            <SolicitudesDropdown />
            {!isParticularContext && <ActiveColegioBadge />}
            <UserSessionMenu
              user={user}
              onLogout={handleLogout}
              extraItems={isParticularContext && isGeneralAdmin ? [{
                key: 'switch-particular',
                label: 'Cambiar particular',
                icon: 'fas fa-exchange-alt',
                onClick: handleChangeParticular
              }] : []}
            />
          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          {/* Alert */}
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          )}

          <div className="new-assistant-content">
            <form onSubmit={handleSubmit} className="assistant-form">
              <div className="assistant-form-grid">
                <div className="assistant-form-panel">
              {/* Información básica */}
              <div className="form-section basic-info-section">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-robot"></i>
                  </div>
                  <div className="section-text">
                    <h3 className="section-title">Información del asistente IA</h3>
                    <p className="section-description">
                      {isEditMode
                        ? 'Actualiza los datos principales del asistente IA para generar la solicitud de edición.'
                        : 'Define el nombre y características básicas de tu asistente IA'}
                    </p>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="gptClasicoNombre" className="form-label">
                    <i className="fas fa-tag"></i>
                    Nombre del asistente IA *
                  </label>
                  <input
                    type="text"
                    id="gptClasicoNombre"
                    className="form-input enhanced"
                    value={formData.gptClasicoNombre}
                    onChange={(e) => handleInputChange('gptClasicoNombre', e.target.value)}
                    placeholder="Ej: Asistente de Álgebra Lineal"
                    maxLength={GPT_CLASICO_NOMBRE_MAX_LENGTH}
                    required
                    disabled={isSubmitting}
                  />
                  <div className="input-hint">
                    <i className="fas fa-lightbulb"></i>
                    Escoge un nombre descriptivo de hasta {GPT_CLASICO_NOMBRE_MAX_LENGTH} caracteres ({formData.gptClasicoNombre.length}/{GPT_CLASICO_NOMBRE_MAX_LENGTH}).
                  </div>
                </div>

                {!isParticularContext && (
                <div className="form-group">
                  <label htmlFor="idMateria" className="form-label">
                    <i className="fas fa-book"></i>
                    Materia (opcional)
                  </label>
                  <select
                    id="idMateria"
                    className="form-select enhanced"
                    value={formData.idMateria}
                    onChange={(e) => handleMateriaChange(e.target.value)}
                    disabled={isSubmitting || materiasLoading}
                  >
                    <option value="">Sin materia (asistente institucional)</option>
                    {materias.map((materia) => {
                      const id = materia.id || materia.Id;
                      return (
                        <option key={id} value={id}>
                          {getMateriaOptionLabel(materia)}
                        </option>
                      );
                    })}
                  </select>
                  {materiasLoading && (
                    <div className="input-hint">
                      <i className="fas fa-spinner fa-spin"></i>
                      {isAdmin ? 'Cargando materias activas del colegio...' : 'Cargando materias asignadas...'}
                    </div>
                  )}
                  {materiasError && (
                    <div className="input-hint" style={{ color: '#dc2626' }}>
                      <i className="fas fa-triangle-exclamation"></i>
                      {materiasError}
                    </div>
                  )}
                  {!materiasLoading && !materiasError && materias.length === 0 && (
                    <div className="input-hint" style={{ color: '#b45309' }}>
                      <i className="fas fa-circle-info"></i>
                      {isAdmin
                        ? 'No hay materias activas en este colegio. Puedes crear un asistente IA institucional sin materia.'
                        : 'No tienes materias asignadas. Puedes crear un asistente IA institucional sin materia.'}
                    </div>
                  )}
                </div>
                )}

                {!isParticularContext && !isEditMode && (
                <div className="form-group">
                  <label htmlFor="idUnidad" className="form-label">
                    <i className="fas fa-layer-group"></i>
                    Unidad (opcional)
                  </label>
                  <select
                    id="idUnidad"
                    className="form-select enhanced"
                    value={formData.idUnidad}
                    onChange={(e) => handleInputChange('idUnidad', e.target.value)}
                    disabled={isSubmitting || unidadesLoading || !formData.idMateria}
                  >
                    <option value="">Sin unidad</option>
                    {unidades.map((unidad) => {
                      const id = unidad.id || unidad.Id;
                      const nombre = unidad.nombre || unidad.Nombre || 'Unidad';
                      return (
                        <option key={id} value={id}>
                          {nombre}
                        </option>
                      );
                    })}
                  </select>
                  {!formData.idMateria && (
                    <div className="input-hint">
                      <i className="fas fa-circle-info"></i>
                      Si dejas la materia vacía, el asistente IA se crea como institucional.
                    </div>
                  )}
                  {unidadesLoading && (
                    <div className="input-hint">
                      <i className="fas fa-spinner fa-spin"></i>
                      Cargando unidades...
                    </div>
                  )}
                  {unidadesError && (
                    <div className="input-hint" style={{ color: '#dc2626' }}>
                      <i className="fas fa-triangle-exclamation"></i>
                      {unidadesError}
                    </div>
                  )}
                  {!unidadesLoading && !unidadesError && formData.idMateria && unidades.length === 0 && (
                    <div className="input-hint" style={{ color: '#b45309' }}>
                      <i className="fas fa-circle-info"></i>
                      No hay unidades activas para esta materia.
                    </div>
                  )}
                </div>
                )}

                <div className="form-group">
                  <label htmlFor="gptClasicoDescripcion" className="form-label">
                    <i className="fas fa-align-left"></i>
                    Descripción del asistente IA
                  </label>
                  <textarea
                    id="gptClasicoDescripcion"
                    className="form-input enhanced"
                    rows="4"
                    value={formData.gptClasicoDescripcion}
                    onChange={(e) => handleInputChange('gptClasicoDescripcion', e.target.value)}
                    placeholder="Describe brevemente el propósito o alcance del asistente IA"
                    disabled={isSubmitting}
                  />
                  <div className="input-hint">
                    <i className="fas fa-info-circle"></i>
                    Esto ayuda a identificar rápidamente el objetivo del asistente IA
                  </div>
                </div>

                <div className="form-section" style={{ marginTop: '2rem' }}>
                  <div className="section-header">
                    <div className="section-icon">
                      <i className="fas fa-sliders-h"></i>
                    </div>
                    <div className="section-text">
                      <h3 className="section-title">Configuración del asistente IA</h3>
                      <p className="section-description">Define las instrucciones iniciales</p>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="gptClasicoFraseIniciarConversacion" className="form-label">
                      <i className="fas fa-comment-dots"></i>
                      Frase para iniciar conversación
                    </label>
                    <input
                      type="text"
                      id="gptClasicoFraseIniciarConversacion"
                      className="form-input enhanced"
                      value={formData.gptClasicoFraseIniciarConversacion}
                      onChange={(e) => handleInputChange('gptClasicoFraseIniciarConversacion', e.target.value)}
                      placeholder="Ej: Hola, ¿en qué puedo ayudarte hoy?"
                      disabled={isSubmitting}
                    />
                  </div>

                </div>

                {!isEditMode && (
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-eye"></i>
                    Visibilidad y acceso
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.publico}
                        onChange={(e) => {
                          const nextValue = e.target.checked;
                          setFormData((prev) => ({
                            ...prev,
                            publico: nextValue,
                            passwordAsistente: nextValue ? '' : prev.passwordAsistente
                          }));
                        }}
                        disabled={isSubmitting}
                      />
                      <span>Asistente IA público</span>
                    </label>
                  </div>
                </div>
                )}

                {!isEditMode && !formData.publico && (
                  <div className="form-group">
                    <label htmlFor="passwordAsistente" className="form-label">
                      <i className="fas fa-key"></i>
                      Password del asistente IA
                    </label>
                    <input
                      id="passwordAsistente"
                      type="text"
                      className="form-input enhanced"
                      value={formData.passwordAsistente}
                      onChange={(e) => handleInputChange('passwordAsistente', e.target.value)}
                      placeholder="Ingresa el password (obligatorio)"
                      disabled={isSubmitting}
                    />
                  </div>
                )}

                {!isEditMode && (
                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-user-lock"></i>
                      Uso del asistente
                    </label>
                    <div
                      style={{
                        display: 'flex',
                        gap: '0.9rem',
                        alignItems: 'flex-start',
                        padding: '1rem 1.1rem',
                        borderRadius: '14px',
                        border: `1px solid ${formData.esPropio ? '#fdba74' : '#bfdbfe'}`,
                        background: formData.esPropio ? '#fff7ed' : '#eff6ff'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.esPropio}
                        onChange={(e) => handleInputChange('esPropio', e.target.checked)}
                        disabled={isSubmitting}
                        style={{ marginTop: '0.15rem' }}
                      />
                      <div style={{ display: 'grid', gap: '0.3rem' }}>
                        <strong style={{ color: '#0f172a' }}>Asistente propio</strong>
                        <span style={{ color: '#475569', lineHeight: 1.5 }}>
                          {formData.esPropio
                            ? 'Este asistente será solo para uso personal del docente. No podrá compartirse con alumnos por medio del aula.'
                            : 'Este asistente podrá ser visible para los estudiantes por medio del aula, según la configuración restante del asistente.'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-section files-section">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-file-pdf"></i>
                  </div>
                  <div className="section-text">
                    <h3 className="section-title">Documentos de Conocimiento</h3>
                    <p className="section-description">
                      {isEditMode
                        ? 'Revisa los PDFs actuales, marca los que deban quitarse y suma nuevos archivos para la solicitud de edición.'
                        : 'Sube los archivos PDF que contengan la información que tu asistente IA debe conocer'}
                    </p>
                  </div>
                </div>

                {isEditMode && (
                  <div className="input-hint">
                    <i className="fas fa-circle-info"></i>
                    El admin general verá exactamente qué archivos se mantienen, cuáles se quitarán y cuáles se agregarán antes de aprobar la edición.
                  </div>
                )}

                {isEditMode && existingFiles.length > 0 && (
                  <div className="selected-files">
                    <h4>Archivos actuales ({existingFiles.length}):</h4>
                    <div className="files-list">
                      {existingFiles.map((file) => {
                        const pendingRemoval = removedExistingFileIds.includes(file.id);
                        return (
                          <div
                            key={file.id}
                            className={`file-item ${pendingRemoval ? 'file-item-pending-removal' : ''}`}
                          >
                            <div className="file-info">
                              <i className="fas fa-file-pdf"></i>
                              <span className="file-name">{file.nombreVisible}</span>
                              <span className={`file-status-pill ${pendingRemoval ? 'pending-removal' : 'active'}`}>
                                {pendingRemoval ? 'Se quitará' : 'Se mantiene'}
                              </span>
                            </div>
                            <button
                              type="button"
                              className={`remove-file-btn ${pendingRemoval ? 'restore-mode' : ''}`}
                              onClick={() => toggleExistingFileRemoval(file.id)}
                              disabled={isSubmitting}
                              title={pendingRemoval ? 'Restaurar archivo en la solicitud' : 'Marcar archivo para quitar'}
                            >
                              <i className={`fas ${pendingRemoval ? 'fa-rotate-left' : 'fa-trash'}`}></i>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {isEditMode && existingFiles.length === 0 && (
                  <div className="input-hint">
                    <i className="fas fa-circle-info"></i>
                    Este asistente IA hoy no tiene archivos asociados. Puedes agregar nuevos PDFs para completar la solicitud.
                  </div>
                )}
                
                <div className="files-upload-container">
                  <div 
                    className={`file-dropzone ${isDragOver ? 'dragover' : ''} ${formData.archivosPdf.length > 0 ? 'has-files' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="dropzone-border">
                      <div className="dropzone-content">
                        <div className="dropzone-icon">
                          <i className="fas fa-cloud-upload-alt"></i>
                        </div>
                        <div className="dropzone-text">
                          <h4>Arrastra y suelta tus archivos PDF aquí</h4>
                          <p>
                            {isEditMode
                              ? 'Los nuevos archivos quedarán adjuntos a la solicitud para que el admin general revise el cambio.'
                              : 'Los archivos se procesarán automáticamente para entrenar a tu asistente IA'}
                          </p>
                        </div>
                        <div className="dropzone-divider">
                          <span>o</span>
                        </div>
                        <label className="file-input-label">
                          <input
                            type="file"
                            multiple
                            accept=".pdf"
                            onChange={handleFileInput}
                            disabled={isSubmitting}
                            style={{ display: 'none' }}
                          />
                          <span className="btn btn-upload">
                            <i className="fas fa-folder-open"></i>
                            Seleccionar Archivos PDF
                          </span>
                        </label>
                        <div className="upload-limits">
                          <div className="limit-item">
                            <i className="fas fa-file-alt"></i>
                            <span>Máximo 20 archivos</span>
                          </div>
                          <div className="limit-item">
                            <i className="fas fa-file-pdf"></i>
                            <span>Solo formato PDF</span>
                          </div>
                          <div className="limit-item">
                            <i className="fas fa-weight-hanging"></i>
                            <span>Hasta 10MB por archivo</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lista de archivos seleccionados */}
                {formData.archivosPdf.length > 0 && (
                  <div className="selected-files">
                    <h4>{isEditMode ? `Archivos nuevos a agregar (${formData.archivosPdf.length}):` : `Archivos seleccionados (${formData.archivosPdf.length}/20):`}</h4>
                    <div className="files-list">
                      {formData.archivosPdf.map((file, index) => (
                        <div key={index} className="file-item">
                          <div className="file-info">
                            <i className="fas fa-file-pdf"></i>
                            <span className="file-name">{file.name}</span>
                            {isEditMode && (
                              <span className="file-status-pill add-mode">Se agregará</span>
                            )}
                            <span className="file-size">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                          <button
                            type="button"
                            className="remove-file-btn"
                            onClick={() => removeFile(index)}
                            disabled={isSubmitting}
                            title="Eliminar archivo"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

                </div>

                <aside className="assistant-prompt-panel">
                  <div className="prompt-panel-header">
                    <i className="fas fa-clipboard-list"></i>
                    <div>
                      <h3>Comportamiento del asistente IA</h3>
                      <p>Resumen del prompt base configurado en el backend.</p>
                    </div>
                  </div>
                  <ul className="prompt-features-list">
                    {PROMPT_FEATURES.map((item) => (
                      <li key={item}>
                        <i className="fas fa-check-circle"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="prompt-extra-field">
                    <label htmlFor="promptExtra" className="form-label">
                      <i className="fas fa-pen"></i>
                      Aclaraciones extra (opcional)
                    </label>
                    <textarea
                      id="promptExtra"
                      className="form-input enhanced"
                      rows="6"
                      value={formData.promptExtra}
                      onChange={(e) => handleInputChange('promptExtra', e.target.value)}
                      placeholder="Ej: prioriza ejemplos resueltos, responde en español neutro..."
                      disabled={isSubmitting}
                    />
                    <div className="input-hint">
                      <i className="fas fa-info-circle"></i>
                      Estas reglas se agregarán al prompt base del asistente IA.
                    </div>
                  </div>
                </aside>
              </div>

              {/* Botones de Acción */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleGoBack}
                  disabled={isSubmitting}
                >
                  <i className="fas fa-arrow-left"></i>
                  Volver a asistentes IA
                </button>
                
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitButtonDisabled}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      {submitLoadingLabel}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-robot"></i>
                      {submitButtonLabel}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
        {successModalOpen && (
          <div className="modal-overlay" onClick={closeSuccessModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">
                  <i className="fas fa-check-circle"></i>
                  {successTitle}
                </h3>
                <button type="button" className="modal-close-btn" onClick={closeSuccessModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <p>{successMessage}</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-primary" onClick={closeSuccessModal}>
                  Entendido
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default NewClassicGptAgent;
